import {
  Calendar,
  ChevronDown,
  Clock,
  ExternalLink,
  Hash,
  MessageSquare,
  Package,
  Presentation,
  Search,
  SlidersHorizontal,
  Tag as TagIcon,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { EmptyState } from '../../../components/EmptyState.js';
import { Button } from '../../../components/ui/button.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../../components/ui/dropdown-menu.js';
import { Input } from '../../../components/ui/input.js';
import { cn } from '../../../lib/utils.js';
import { useTags } from '../../common/admin-hooks.js';
import { TeamsDropdown, TEAM_ALL } from '../../common/components/TeamsDropdown.js';
import { ApplicationIcon } from '../../scenario/components/ApplicationIcon.js';
import { FusionRequestSheet } from '../../scenario/components/FusionRequestSheet.js';
import { FOLDER_ALL, FOLDER_UNCATEGORIZED } from '../../scenario/components/ScenarioPageHeader.js';
import { TagChip } from '../../scenario/components/TagChip.js';
import { TagsManageDialog } from '../../scenario/components/TagsManageDialog.js';
import { useScenariosList } from '../../scenario/hooks.js';
import type { ScenarioApplication, ScenarioEventSchedule } from '../../scenario/types.js';
import { formatDateTime, formatSchedule } from '../../scenario/utils.js';
import {
  EMPTY_FILTERS,
  PresentationDataFiltersSheet,
  type PresentationFilters,
} from '../components/PresentationDataFiltersSheet.js';

/**
 * Presentation list — scenarios grouped by attached tag, with an
 * optional tag-filter chip strip. Mirrors the legacy
 * `wf-audit/src/slices/presentation/components/presentation/Provider.tsx`
 * group-by-tag behaviour. Untagged scenarios trail in a "Not Tagged"
 * section. Each card routes to `/presentation/:id`.
 *
 * Tags + scenario.tags are both inline today (engine returns
 * `tags[]` on every scenario list item), so no extra fetch is
 * needed for the grouping — the chip strip uses `/v1/tags` to list
 * the full tag catalog so empty tags still surface.
 */
interface TagInfo {
  id: number;
  title: string;
  color: string | null;
  icon: string | null;
}

interface ScenarioCard {
  id: string;
  name: string;
  folder: string;
  /** Owning team's Fusion id (or `''` if untagged). Drives the
   *  Teams dropdown filter on the toolbar — same key the
   *  Scenarios / Dashboard / Value pages use. */
  teamFusionId: string;
  tagIds: ReadonlySet<number>;
  /** Module count for the legacy "Modules: N" badge. */
  totalModules: number;
  /** First ~3 lines of the operator-authored description (or the
   *  AI summary as fallback) for the card body. */
  description: string;
  /** Slug+label pairs for the app-icon strip — same shape the
   *  `ApplicationIcon` component reads. */
  applications: ReadonlyArray<ScenarioApplication>;
  /** Cadence label (legacy "Each 5 minutes" / "On demand" / etc.). */
  scheduleLabel: string;
  /** ISO modified timestamp for the footer date. */
  modifiedAt: string;
  /** Whether the scenario is active ('On'); drives the All/Active/
   *  Inactive counter tabs and the inactive-badge styling. */
  active: boolean;
  /** Whether the scenario has the `hasNotifications` flag set —
   *  drives the Data Filters sidebar's Has Notifications toggle. */
  hasNotifications: boolean;
  /** ISO timestamp of the last successful run, used by the Data
   *  Filters sidebar's Last Run before/on/after date filter. */
  lastRunAt: string | null;
}

type StatusFilter = 'all' | 'active' | 'inactive';

const UNTAGGED_KEY = '__untagged__';

export function PresentationListPage(): JSX.Element {
  const { data: rows = [], isLoading, error } = useScenariosList();
  const { data: tagRows = [] } = useTags();
  const [search, setSearch] = useState('');
  const [selectedTagIds, setSelectedTagIds] = useState<ReadonlyArray<number | typeof UNTAGGED_KEY>>(
    [],
  );
  const [status, setStatus] = useState<StatusFilter>('all');
  // Folder + Team filters — URL-pinned with the same sentinel
  // pattern as the Scenarios / Value Metrics / Dashboard pages so
  // an operator can deep-link a particular view.
  const [params, setParams] = useSearchParams();
  const folder = params.get('folder') ?? FOLDER_ALL;
  const team = params.get('team') ?? TEAM_ALL;
  function setFolder(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === FOLDER_ALL) ps.delete('folder');
    else ps.set('folder', next);
    setParams(ps);
  }
  function setTeam(next: string): void {
    const ps = new URLSearchParams(params);
    if (next === TEAM_ALL) ps.delete('team');
    else ps.set('team', next);
    setParams(ps);
  }
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filters, setFilters] = useState<PresentationFilters>(EMPTY_FILTERS);
  const [fusionRequestOpen, setFusionRequestOpen] = useState(false);
  // Manage Tags entry — opens `TagsManageDialog`, which lists every
  // license tag with edit + delete affordances + a "New Tag" button.
  // Same surface the Presentation detail rail uses; operators reach
  // it from both the landing page and the per-scenario detail view.
  const [manageTagsOpen, setManageTagsOpen] = useState(false);

  const allTags = useMemo<ReadonlyArray<TagInfo>>(
    () =>
      tagRows.flatMap((r) => {
        if (!r || typeof r !== 'object') return [];
        const id = typeof r.id === 'number' ? r.id : null;
        const title = typeof r.title === 'string' ? r.title : null;
        if (id === null || title === null) return [];
        return [
          {
            id,
            title,
            color: typeof r.color === 'string' ? r.color : null,
            icon: typeof r.icon === 'string' ? r.icon : null,
          },
        ];
      }),
    [tagRows],
  );
  const tagById = useMemo(() => {
    const m = new Map<number, TagInfo>();
    for (const t of allTags) m.set(t.id, t);
    return m;
  }, [allTags]);

  const cards = useMemo<ReadonlyArray<ScenarioCard>>(() => {
    return rows.flatMap((row) => {
      const id = (row.fusionId ?? row.fusion_id ?? row.id) as string | number | undefined;
      if (id === undefined || id === null) return [];
      const name = (row.name as string | undefined) ?? `Scenario #${String(id)}`;
      const folder = (row.folderName as string | undefined) ?? '';
      const teamFusionId = (row.teamFusionId as string | undefined) ?? '';
      const tagsArr = Array.isArray(row.tags) ? (row.tags as Array<{ id?: unknown }>) : [];
      const tagIds = new Set<number>();
      for (const t of tagsArr) {
        if (typeof t?.id === 'number') tagIds.add(t.id);
      }
      const totalModules = typeof row.totalModules === 'number' ? row.totalModules : 0;
      // Operator-authored description first; fall back to the AI
      // business summary when there's no human-written copy. (The
      // technical summary no longer ships on the list response —
      // payload size; only the business summary stays inline.) The
      // card clamps to 3 lines via the body's `line-clamp-3`.
      const description =
        (row.description as string | undefined) ??
        (row.aiBusinessSummary as string | undefined) ??
        '';
      const applications = Array.isArray(row.applications)
        ? (row.applications as ScenarioApplication[])
        : [];
      const scheduleLabel = formatSchedule(
        (row.eventSchedule as ScenarioEventSchedule | undefined) ?? undefined,
      );
      const modifiedAt = (row.modifiedAt as string | undefined) ?? '';
      // Status is the legacy `'On' | 'Off'` enum on the row.
      const active = row.status === 'On';
      const hasNotifications =
        typeof row.hasNotifications === 'boolean' ? row.hasNotifications : false;
      const lastRunAt = (row.lastSuccessfulRunAt as string | undefined) ?? null;
      return [
        {
          id: String(id),
          name,
          folder,
          teamFusionId,
          tagIds,
          totalModules,
          description,
          applications,
          scheduleLabel,
          modifiedAt,
          active,
          hasNotifications,
          lastRunAt,
        },
      ];
    });
  }, [rows]);

  // App-types in the unfiltered list — populates the Applications
  // multi-select inside the Data Filters sheet. Stable across the
  // page lifetime since it's derived from the engine response.
  const allApplicationTypes = useMemo(() => {
    const s = new Set<string>();
    for (const c of cards) {
      for (const a of c.applications) {
        if (typeof a.type === 'string' && a.type) s.add(a.type);
      }
    }
    return [...s];
  }, [cards]);
  const maxModules = useMemo(() => {
    let mx = 0;
    for (const c of cards) if (c.totalModules > mx) mx = c.totalModules;
    return mx;
  }, [cards]);

  // Distinct folder names actually present on the license's
  // scenarios — drives the folder dropdown options. Sorted
  // alphabetically for stable order across renders.
  const folderOptions = useMemo<ReadonlyArray<string>>(() => {
    const names = new Set<string>();
    for (const c of cards) {
      if (c.folder && c.folder.length > 0) names.add(c.folder);
    }
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [cards]);

  const folderLabel =
    folder === FOLDER_ALL
      ? 'All scenarios'
      : folder === FOLDER_UNCATEGORIZED
        ? 'Uncategorized'
        : folder;

  // Folder + Team narrowing — runs first since the dropdowns sit
  // at the top of the page and a narrow folder choice may eliminate
  // most rows up-front. Matches the other pages' filter ordering.
  const folderTeamFiltered = useMemo(() => {
    return cards.filter((c) => {
      if (folder === FOLDER_UNCATEGORIZED) {
        if (c.folder.length > 0) return false;
      } else if (folder !== FOLDER_ALL) {
        if (c.folder !== folder) return false;
      }
      if (team !== TEAM_ALL && c.teamFusionId !== team) return false;
      return true;
    });
  }, [cards, folder, team]);

  // Text-search filter applies after folder/team, then tag filter.
  const searchFiltered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return folderTeamFiltered;
    return folderTeamFiltered.filter((c) => c.name.toLowerCase().includes(q));
  }, [folderTeamFiltered, search]);

  // Tag filter: when any chip is selected, show only matching cards.
  // "Untagged" matches cards with zero tags.
  const tagFiltered = useMemo(() => {
    if (selectedTagIds.length === 0) return searchFiltered;
    return searchFiltered.filter((c) => {
      for (const sel of selectedTagIds) {
        if (sel === UNTAGGED_KEY) {
          if (c.tagIds.size === 0) return true;
        } else if (c.tagIds.has(sel)) {
          return true;
        }
      }
      return false;
    });
  }, [searchFiltered, selectedTagIds]);

  // Data Filters sidebar: modules range / has-notifications /
  // applications multi-select / capabilities text / last-run date.
  // Applies after search + tag chip + the legacy primary narrowing,
  // but before the status pills so the All/Active/Inactive counts
  // reflect what the operator would actually see when toggling them.
  const advancedFiltered = useMemo(() => {
    const capsQ = filters.capabilities.trim().toLowerCase();
    const lastRunAt = filters.lastRunDate ? new Date(filters.lastRunDate).getTime() : null;
    return tagFiltered.filter((c) => {
      if (c.totalModules < filters.modulesMin) return false;
      if (filters.modulesMax > 0 && c.totalModules > filters.modulesMax) return false;
      if (filters.hasNotifications && !c.hasNotifications) return false;
      if (filters.applicationTypes.length > 0) {
        const types = new Set(c.applications.map((a) => a.type ?? '').filter(Boolean));
        if (!filters.applicationTypes.some((t) => types.has(t))) return false;
      }
      if (capsQ && !c.description.toLowerCase().includes(capsQ)) return false;
      if (lastRunAt !== null) {
        if (!c.lastRunAt) return false;
        const ts = new Date(c.lastRunAt).getTime();
        if (Number.isNaN(ts)) return false;
        if (filters.lastRunComparator === 'before' && !(ts < lastRunAt)) return false;
        if (filters.lastRunComparator === 'after' && !(ts > lastRunAt)) return false;
        if (filters.lastRunComparator === 'on') {
          // Same calendar day in the operator's local timezone.
          const filterDay = new Date(filters.lastRunDate!).toDateString();
          if (new Date(c.lastRunAt).toDateString() !== filterDay) return false;
        }
      }
      return true;
    });
  }, [tagFiltered, filters]);

  // Counts for the All/Active/Inactive pills — computed off the
  // post-advanced-filter set so the numbers always match what the
  // operator would see by toggling the pill.
  const counts = useMemo(() => {
    let active = 0;
    let inactive = 0;
    for (const c of advancedFiltered) {
      if (c.active) active += 1;
      else inactive += 1;
    }
    return { all: advancedFiltered.length, active, inactive };
  }, [advancedFiltered]);

  // Final narrowing: pill choice applied last.
  const filtered = useMemo(() => {
    if (status === 'all') return advancedFiltered;
    return advancedFiltered.filter((c) => (status === 'active' ? c.active : !c.active));
  }, [advancedFiltered, status]);

  // Group post-filter cards by their tag set. A card with multiple
  // tags appears in each group; an untagged card lands in the trailing
  // "Not Tagged" section.
  const groups = useMemo(() => {
    interface Group {
      key: string;
      tag: TagInfo | null;
      cards: ScenarioCard[];
    }
    const byTag = new Map<number, Group>();
    const untagged: Group = { key: UNTAGGED_KEY, tag: null, cards: [] };
    for (const c of filtered) {
      if (c.tagIds.size === 0) {
        untagged.cards.push(c);
        continue;
      }
      for (const tid of c.tagIds) {
        let g = byTag.get(tid);
        if (!g) {
          const tag = tagById.get(tid);
          if (!tag) continue; // dangling tag id — skip silently
          g = { key: String(tid), tag, cards: [] };
          byTag.set(tid, g);
        }
        g.cards.push(c);
      }
    }
    const sortedByTag = [...byTag.values()].sort((a, b) =>
      (a.tag?.title ?? '').localeCompare(b.tag?.title ?? ''),
    );
    const out: Group[] = sortedByTag;
    if (untagged.cards.length > 0) out.push(untagged);
    return out;
  }, [filtered, tagById]);

  const toggleTag = (key: number | typeof UNTAGGED_KEY): void => {
    setSelectedTagIds((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key],
    );
  };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <header className="flex flex-wrap items-center gap-3 px-6 pb-2 pt-6">
        <h1 className="text-2xl font-light text-foreground">Presentation</h1>
        <span className="text-foreground/30">|</span>
        {/* Folder dropdown — same trigger + sentinels as the
            Scenarios / Value Metrics / Dashboard pages. */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1.5 rounded-md border border-transparent px-2 py-1 text-base text-foreground/80 transition-colors hover:border-foreground/15 hover:bg-foreground/5 hover:text-foreground"
            >
              <span>{folderLabel}</span>
              <ChevronDown className="h-4 w-4 text-foreground/50" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="max-h-[60vh] min-w-[220px] overflow-y-auto">
            <DropdownMenuLabel>Folder</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_ALL}
              onCheckedChange={() => setFolder(FOLDER_ALL)}
            >
              All scenarios
            </DropdownMenuCheckboxItem>
            <DropdownMenuCheckboxItem
              checked={folder === FOLDER_UNCATEGORIZED}
              onCheckedChange={() => setFolder(FOLDER_UNCATEGORIZED)}
            >
              Uncategorized
            </DropdownMenuCheckboxItem>
            {folderOptions.length > 0 ? <DropdownMenuSeparator /> : null}
            {folderOptions.map((name) => (
              <DropdownMenuCheckboxItem
                key={name}
                checked={folder === name}
                onCheckedChange={() => setFolder(name)}
              >
                {name}
              </DropdownMenuCheckboxItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        <div className="flex-1" />
        {/* "Fusion Request" header button — opens a `FusionRequestSheet`
            with no scenario context (matches legacy
            `wf-audit/.../header/Provider.tsx` Fusion Request entry-
            point that operators reach for when filing a request not
            tied to a specific scenario row). */}
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => setFusionRequestOpen(true)}
          className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
        >
          <MessageSquare className="mr-1.5 h-3.5 w-3.5" /> Fusion Request
        </Button>
      </header>

      <div className="space-y-3 border-b border-foreground/10 bg-foreground/[0.03] px-6 py-3">
        {/* 3-col grid `[1fr][auto][1fr]` — search + drawer + Manage
            Tags on the left, status pills in the middle column for
            geometric centering against the card grid below, Teams
            dropdown on the right. Matches the layout shipped on the
            Scenarios / Value Metrics / Dashboard pages. */}
        <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <Input
              type="search"
              placeholder="Search scenarios..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="max-w-sm border-foreground/15 bg-foreground/5 text-foreground placeholder:text-foreground/40 focus-visible:ring-white/30"
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFiltersOpen(true)}
              className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
              title="Open advanced filters"
              aria-label="Open advanced filters"
            >
              <SlidersHorizontal className="h-4 w-4" />
            </Button>
            {/* Manage Tags — opens `TagsManageDialog`, listing every
                license tag with inline edit + delete actions and a
                "New Tag" button. Mirrors the Presentation detail rail's
                entry point so both surfaces share the same management
                experience. */}
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setManageTagsOpen(true)}
              className="border-foreground/15 text-foreground/80 hover:border-foreground/40 hover:text-foreground"
              title="Manage tags"
            >
              <TagIcon className="mr-1.5 h-3.5 w-3.5" /> Manage Tags
            </Button>
          </div>
          {/* All / Active / Inactive counter pills — legacy parity. */}
          <div className="inline-flex rounded-md border border-foreground/15 bg-foreground/[0.04] p-1">
            {(['all', 'active', 'inactive'] as const).map((k) => {
              const sel = status === k;
              const count =
                k === 'all' ? counts.all : k === 'active' ? counts.active : counts.inactive;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => setStatus(k)}
                  className={cn(
                    'flex items-center gap-2 rounded px-3 py-1 text-sm capitalize transition-colors',
                    sel
                      ? 'bg-foreground/15 text-foreground'
                      : 'text-foreground/70 hover:text-foreground',
                  )}
                >
                  <span>{k}</span>
                  <span
                    className={cn(
                      'rounded px-1.5 py-0.5 text-xs',
                      sel
                        ? 'bg-foreground/20 text-foreground'
                        : 'bg-foreground/10 text-foreground/60',
                    )}
                  >
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="flex min-w-0 items-center justify-end">
            <TeamsDropdown team={team} onTeamChange={setTeam} />
          </div>
        </div>

        {allTags.length > 0 || cards.some((c) => c.tagIds.size === 0) ? (
          <div className="flex flex-wrap items-center gap-1.5">
            <TagIcon className="h-3.5 w-3.5 text-foreground/40" />
            {allTags.map((t) => {
              const active = selectedTagIds.includes(t.id);
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => toggleTag(t.id)}
                  className={cn(
                    'rounded-full transition-all',
                    active
                      ? 'ring-2 ring-[color:var(--brand-yellow)] ring-offset-2 ring-offset-[color:var(--surface-overlay)]'
                      : 'opacity-70 hover:opacity-100',
                  )}
                >
                  {/* Filter pill = same `TagChip` chip every other
                      surface renders, with a yellow outline ring on
                      the active state so the toggle reads at a
                      glance against the tag's saved colour. */}
                  <TagChip tag={t} size="sm" />
                </button>
              );
            })}
            <button
              type="button"
              onClick={() => toggleTag(UNTAGGED_KEY)}
              className={cn(
                'rounded-full border px-2.5 py-0.5 text-xs italic transition-colors',
                selectedTagIds.includes(UNTAGGED_KEY)
                  ? 'border-[color:var(--brand-yellow)] bg-foreground/10 text-foreground'
                  : 'border-foreground/15 bg-transparent text-foreground/50 hover:border-foreground/40 hover:text-foreground/70',
              )}
            >
              Untagged
            </button>
            {selectedTagIds.length > 0 ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setSelectedTagIds([])}
                className="ml-1 h-6 px-2 text-xs text-foreground/60 hover:bg-foreground/10 hover:text-foreground"
              >
                <X className="mr-1 h-3 w-3" /> Clear
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className="px-6 py-4">
        {error ? (
          <div className="rounded-md border border-rose-400/30 bg-rose-500/10 p-4 text-sm text-rose-800 dark:text-rose-200">
            {error.message}
          </div>
        ) : isLoading ? (
          <div className="rounded-md border border-foreground/10 bg-foreground/[0.02] p-8 text-center text-sm text-foreground/50">
            Loading…
          </div>
        ) : filtered.length === 0 ? (
          rows.length === 0 ? (
            <EmptyState
              icon={Presentation}
              tint="amber"
              title="No scenarios yet"
              description="Scenarios appear here once the engine catalog picks them up. Once they do, each one gets a slide-friendly Present link."
            />
          ) : (
            <EmptyState
              icon={Search}
              title="No scenarios match your filters"
              description={
                <>
                  {rows.length} scenario{rows.length === 1 ? '' : 's'} hidden — clear the search /
                  tag / advanced filters to see everything.
                </>
              }
            />
          )
        ) : (
          <div className="space-y-4">
            {groups.map((g) => (
              <section
                key={g.key}
                className="rounded-md border border-foreground/10 bg-foreground/[0.02]"
              >
                <header className="flex items-center gap-2 border-b border-foreground/10 px-4 py-2">
                  {g.tag ? (
                    <TagChip tag={g.tag} size="md" />
                  ) : (
                    <h2 className="text-sm font-semibold italic text-foreground/60">Not tagged</h2>
                  )}
                  <span className="text-xs text-foreground/40">({g.cards.length})</span>
                </header>
                <ul className="grid grid-cols-1 gap-3 p-3 md:grid-cols-2 xl:grid-cols-3">
                  {g.cards.map((c) => (
                    <li key={c.id}>
                      <PresentationCard card={c} tagColor={g.tag?.color ?? null} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </div>

      <PresentationDataFiltersSheet
        open={filtersOpen}
        onOpenChange={setFiltersOpen}
        filters={filters}
        onChange={setFilters}
        allTags={allTags}
        allApplicationTypes={allApplicationTypes}
        maxModules={maxModules}
      />
      <FusionRequestSheet open={fusionRequestOpen} onOpenChange={setFusionRequestOpen} />
      <TagsManageDialog open={manageTagsOpen} onOpenChange={setManageTagsOpen} />
    </div>
  );
}

/**
 * One scenario card on the presentation list. Mirrors the legacy
 * `wf-audit/.../presentationThumb/Provider.tsx` two-section layout:
 *
 *   ┌──────────────────────────────────────────────────────────┐
 *   │  Title                              [app icons … +N]     │  ← tag-tinted header
 *   │  📦 Modules: N    # ID: 101001                            │
 *   ├──────────────────────────────────────────────────────────┤
 *   │  Description text, clamped to 3 lines so cards stay     │  ← body
 *   │  the same height across a row.                           │
 *   │                                                          │
 *   │  ⏱ Each 5 minutes              📅 05/18/2026 12:30 AM    │  ← footer
 *   └──────────────────────────────────────────────────────────┘
 *
 * Tag color (when the card lives in a single-tag group) tints the
 * full card surface at ~15% opacity — matches the legacy walkthrough-2
 * affordance (`sec_075`, `sec_080`) where every card under a tag
 * group reads at a glance against the tag's saved colour.
 */
function PresentationCard({
  card,
  tagColor,
}: {
  card: ScenarioCard;
  tagColor: string | null;
}): JSX.Element {
  const visibleApps = card.applications.slice(0, 6);
  const overflowApps = Math.max(0, card.applications.length - visibleApps.length);
  // 15% opacity tint over the card surface. Robust to `#rgb` /
  // `#rrggbb` / `rgb()` / `rgba()` inputs.
  const cardTint = tintColor(tagColor, 0.15);
  return (
    <Link
      to={`/presentation/${encodeURIComponent(card.id)}`}
      className="group flex h-full flex-col overflow-hidden rounded-xl border border-foreground/10 bg-foreground/[0.03] transition-colors hover:border-[color:var(--brand-yellow)] hover:bg-foreground/[0.06]"
      style={cardTint ? { backgroundColor: cardTint } : undefined}
    >
      <div className="flex flex-col gap-1.5 border-b border-foreground/10 p-3">
        <div className="flex items-start justify-between gap-2">
          <h3 className="line-clamp-2 flex-1 text-sm font-semibold text-foreground">{card.name}</h3>
          {visibleApps.length > 0 || overflowApps > 0 ? (
            <div className="flex shrink-0 -space-x-2">
              {visibleApps.map((app, i) => (
                <div
                  key={`${app.type ?? 'app'}-${i}`}
                  className="rounded-full ring-2 ring-[color:var(--surface-overlay)]"
                  title={app.label ?? app.type ?? 'Application'}
                >
                  <ApplicationIcon app={app} size={28} sizeIcon={22} />
                </div>
              ))}
              {overflowApps > 0 ? (
                <div
                  className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground/30 text-[10px] font-medium text-foreground ring-2 ring-[color:var(--surface-overlay)]"
                  title={`+${overflowApps} more`}
                >
                  +{overflowApps}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-foreground/65">
          <span className="inline-flex items-center gap-1">
            <Package className="h-3.5 w-3.5" /> Modules: {card.totalModules}
          </span>
          <span className="inline-flex items-center gap-1 font-mono">
            <Hash className="h-3.5 w-3.5" /> {card.id}
          </span>
          {card.folder ? (
            <span className="inline-flex items-center gap-1 truncate text-foreground/55">
              · {card.folder}
            </span>
          ) : null}
        </div>
      </div>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <p className="line-clamp-3 flex-1 text-sm leading-relaxed text-foreground/80">
          {card.description || (
            <span className="italic text-foreground/45">No description available</span>
          )}
        </p>
        <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-foreground/55">
          {card.scheduleLabel ? (
            <span className="inline-flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> {card.scheduleLabel}
            </span>
          ) : (
            <span />
          )}
          {card.modifiedAt ? (
            <span className="inline-flex items-center gap-1">
              <Calendar className="h-3.5 w-3.5" /> {formatDateTime(card.modifiedAt)}
            </span>
          ) : null}
          <ExternalLink className="h-3.5 w-3.5 text-foreground/40 opacity-0 transition-opacity group-hover:opacity-100" />
        </div>
      </div>
    </Link>
  );
}

/**
 * Return a CSS color string at `alpha` opacity. Handles the three
 * formats the tag-color column can carry: `#rgb`, `#rrggbb`, and
 * (legacy) `rgb()` / `rgba()`. Returns `null` when input is null
 * or unrecognized so the caller can fall back to a neutral wash.
 */
function tintColor(color: string | null, alpha: number): string | null {
  if (!color) return null;
  const hexMatch = color.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (hexMatch) {
    const hex = hexMatch[1]!;
    const full =
      hex.length === 3
        ? hex
            .split('')
            .map((c) => c + c)
            .join('')
        : hex;
    const r = parseInt(full.slice(0, 2), 16);
    const g = parseInt(full.slice(2, 4), 16);
    const b = parseInt(full.slice(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  const rgbMatch = color.match(/^rgba?\(([^)]+)\)$/i);
  if (rgbMatch) {
    const parts = rgbMatch[1]!.split(',').map((s) => s.trim());
    if (parts.length >= 3) {
      const [r, g, b] = parts;
      return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }
  }
  return null;
}
